import { dateLocale } from "@/lib/dateTime";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Plus, RefreshCw, Search } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { toQuery } from "@/lib/filters";
import { useAuth } from "@/context/AuthContext";
import { ListPager } from "@/components/ui/list-pager";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { cn, formatINR } from "@/lib/utils";
import { AgreementStatusBadge, AGREEMENT_STATUS_LABELS } from "@/components/agreements/AgreementStatusBadge";
import { NewAgreementDialog } from "@/components/agreements/NewAgreementDialog";
import { ListEmpty, PageHeader, Person, ViewsPanel } from "@/components/patterns";
import { AGREEMENT_TYPE_LABELS, type AgreementListRow, type AgreementStatus, type AgreementSummary } from "@/types/agreement";

/** "Ending soon" looks at executed agreements ending within this many days */
const ENDING_SOON_DAYS = 60;

type Tab = "ALL" | "ENDING_SOON" | "MINE_TO_REVIEW" | AgreementStatus;

const STATUS_TABS: AgreementStatus[] = ["DRAFT", "IN_REVIEW", "APPROVED", "SENT", "VIEWED", "PARTIALLY_SIGNED", "EXECUTED", "DECLINED", "EXPIRED", "TERMINATED", "SUPERSEDED"];

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";

const filterFor = (tab: Tab) => ({
  status: tab !== "ALL" && tab !== "ENDING_SOON" && tab !== "MINE_TO_REVIEW" ? tab : undefined,
  ending_within: tab === "ENDING_SOON" ? ENDING_SOON_DAYS : undefined,
  mine_to_review: tab === "MINE_TO_REVIEW" ? "true" : undefined,
});

export function AgreementsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("agreement.create");
  const canReview = hasPermission("agreement.approve");

  const [tab, setTab] = useState<Tab>("ALL");
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const list = usePagedList<AgreementListRow>({
    deps: [tab, search],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => unwrapPage<AgreementListRow>(await api.get(`/api/Agreement/List${toQuery({ ...filterFor(tab), search: search.trim(), page, page_size: 30 })}`)),
  });

  // What is in force and what needs attention, above the list
  const [summary, setSummary] = useState<AgreementSummary | null>(null);
  useEffect(() => {
    api
      .get("/api/Agreement/Summary")
      .then((res) => setSummary(unwrapObject<AgreementSummary>(res)))
      .catch(() => setSummary(null));
  }, [list.total]);

  // Counts beside each view in the panel
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    const keys: Tab[] = ["ALL", "ENDING_SOON", ...(canReview ? (["MINE_TO_REVIEW"] as Tab[]) : []), ...STATUS_TABS];
    Promise.all(
      keys.map((k) =>
        api
          .get(`/api/Agreement/List${toQuery({ ...filterFor(k), page: 1, page_size: 1 })}`)
          .then((r) => [k, unwrapPage(r).total] as const)
          .catch(() => [k, 0] as const),
      ),
    ).then((rows) => setCounts(Object.fromEntries(rows)));
  }, [list.total, canReview]);

  const title = tab === "ALL" ? "All agreements" : tab === "ENDING_SOON" ? `Ending within ${ENDING_SOON_DAYS} days` : tab === "MINE_TO_REVIEW" ? "Needs my review" : AGREEMENT_STATUS_LABELS[tab];

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Agreements"
        saved={{
          module: "agreements",
          getConfig: () => ({ tab, search }),
          apply: (c) => {
            setTab((c.tab === "ALL" || c.tab === "ENDING_SOON" || c.tab === "MINE_TO_REVIEW" || STATUS_TABS.includes(c.tab) ? c.tab : "ALL") as Tab);
            setSearch(c.search || "");
          },
        }}
        subtitle="Contracts with customers"
        active={tab}
        onSelect={(k) => setTab(k as Tab)}
        groups={[
          {
            items: [
              { key: "ALL", label: "All agreements", count: counts.ALL },
              { key: "MINE_TO_REVIEW", label: "Needs my review", count: counts.MINE_TO_REVIEW, tone: "warning", hidden: !canReview },
              { key: "ENDING_SOON", label: `Ending within ${ENDING_SOON_DAYS} days`, count: counts.ENDING_SOON, tone: "warning" },
            ],
          },
          { heading: "Status", items: STATUS_TABS.map((s) => ({ key: s, label: AGREEMENT_STATUS_LABELS[s], count: counts[s] })) },
        ]}
      />

      <PageHeader
        title={title}
        summary={list.total}
        actions={
          <>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={list.reload} disabled={list.loading}>
              <RefreshCw className={cn("size-3.5 mr-1.5", list.loading && "animate-spin")} />
              Refresh
            </Button>
            {canCreate && (
              <Button size="sm" onClick={() => setNewOpen(true)}>
                <Plus className="size-4 mr-1.5" />
                New agreement
              </Button>
            )}
          </>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {(
            [
              ["In force", String(summary.in_force.count), formatINR(summary.in_force.value), "EXECUTED"],
              ["Out for signature", String(summary.awaiting_signature.count), formatINR(summary.awaiting_signature.value), "PARTIALLY_SIGNED"],
              ["Ending within 30 days", String(summary.ending_30), `${summary.ending_60} within 60 · ${summary.ending_90} within 90`, "ENDING_SOON"],
              ["Waiting for review", String(summary.waiting_review), "needs an approver", "IN_REVIEW"],
            ] as const
          ).map(([label, big, small, to]) => (
            <button key={label} type="button" onClick={() => setTab(to as Tab)} className="rounded-lg border border-border bg-card px-3.5 py-2.5 text-left hover:bg-muted/50">
              <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
              <div className="text-xl font-bold leading-tight">{big}</div>
              <div className="truncate text-[11px] text-muted-foreground">{small}</div>
            </button>
          ))}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by number, title, customer or deal..."
          className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm shadow-xs"
        />
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agreement</TableHead>
                <TableHead>Customer / Deal</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Term</TableHead>
                <TableHead className="text-right">Contract Value</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/agreements/${r.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-primary/10 text-accent-foreground flex items-center justify-center shrink-0">
                        <FileSignature className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm font-mono">
                          {r.display_number}
                          {r.amends_agreement_id && <span className="ml-1.5 rounded bg-primary/10 px-1.5 py-0.5 align-middle font-sans text-[10px] font-semibold uppercase text-primary">Amendment</span>}
                          {r.source === "UPLOADED" && <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 align-middle font-sans text-[10px] font-semibold uppercase text-muted-foreground">Uploaded</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">{r.title} · {AGREEMENT_TYPE_LABELS[r.agreement_type] || r.agreement_type}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.account?.name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{r.deal?.name}</div>
                  </TableCell>
                  <TableCell><Person name={r.owner ? `${r.owner.first_name} ${r.owner.last_name || ""}`.trim() : ""} /></TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.effective_date)} → {fmtDate(r.end_date)}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatINR(r.contract_value)}</TableCell>
                  <TableCell><AgreementStatusBadge status={r.agreement_status} /></TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="p-0">
                    <ListEmpty
                      noun="agreements" error={list.error} onRetry={list.reload}
                      icon={FileSignature}
                      filtered={!!search.trim() || tab !== "ALL"}
                      onClear={() => {
                        setSearch("");
                        setTab("ALL");
                      }}
                      createLabel="+ New agreement"
                      onCreate={canCreate ? () => setNewOpen(true) : undefined}
                      firstUse="Start one from a deal whose quotation the customer has accepted."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <NewAgreementDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
